import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { kontakteApi } from '../api';

const ROLLEN = ['Kundenservice','Vertragsservice','Kündigungsstelle','Ansprechpartner','Makler','Vertreter','Buchhaltung','Datenschutz','technischer Support','Vertrieb','sonstiges'];

export default function KontaktDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [kontakt, setKontakt] = useState(null);
  const [historie, setHistorie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(0);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const [k, h] = await Promise.all([kontakteApi.get(id), kontakteApi.historie(id)]);
      setKontakt(k); setForm(k);
      setHistorie(h.eintraege || []);
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await kontakteApi.update(id, form);
      setEditMode(false); load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  };

  const handleSetPrimaer = async () => {
    try { await kontakteApi.setPrimaer(id); load(); } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Kontakt wirklich löschen?')) return;
    try { await kontakteApi.delete(id); navigate('/kontakte'); } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6366f1' }}>⏳ Lade…</div>;
  if (err) return <div style={{ padding: 20, color: '#f87171' }}>{err}</div>;
  if (!kontakt) return null;

  const initials = ([kontakt.vorname, kontakt.nachname].filter(Boolean).map(s => s[0]).join('') || '?').toUpperCase();

  return (
    <div style={{ padding: 20, color: '#f1f5f9', maxWidth: 900 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 20, color: '#818cf8', flexShrink: 0 }}>
          {initials}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>{[kontakt.vorname, kontakt.nachname].filter(Boolean).join(' ')}</h1>
            {kontakt.ist_primaer ? <span style={{ background: '#1e3a5f', color: '#818cf8', fontSize: 11, padding: '2px 8px', borderRadius: 4 }}>★ Primärkontakt</span> : null}
          </div>
          <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
            {kontakt.rolle}{kontakt.abteilung ? ` · ${kontakt.abteilung}` : ''}{kontakt.anbieter_name ? ` · ${kontakt.anbieter_name}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!kontakt.ist_primaer && <button onClick={handleSetPrimaer} className="btn-secondary">Als Primärkontakt</button>}
          <button onClick={() => setEditMode(!editMode)} style={btn(editMode ? '#334155' : '#818cf8')}>{editMode ? 'Abbrechen' : 'Bearbeiten'}</button>
          <button onClick={() => navigate('/kontakte')} className="btn-ghost">← Zurück</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #334155' }}>
        {['Details', 'Historie'].map((t, i) => (
          <button key={t} onClick={() => setTab(i)} style={{
            background: tab === i ? '#818cf8' : 'transparent', color: tab === i ? '#fff' : '#94a3b8',
            border: 'none', padding: '8px 14px', cursor: 'pointer', fontSize: 13, borderRadius: '6px 6px 0 0', fontWeight: tab === i ? 600 : 400
          }}>{t}</button>
        ))}
      </div>

      {/* Tab 0: Details / Edit */}
      {tab === 0 && (
        editMode ? (
          <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 20 }}>
            <h2 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600 }}>Kontakt bearbeiten</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Vorname"><input value={form.vorname || ''} onChange={e => setForm(p => ({...p, vorname: e.target.value}))} style={inputStyle} /></Field>
              <Field label="Nachname *"><input required value={form.nachname || ''} onChange={e => setForm(p => ({...p, nachname: e.target.value}))} style={inputStyle} /></Field>
              <Field label="E-Mail"><input type="email" value={form.email || ''} onChange={e => setForm(p => ({...p, email: e.target.value}))} style={inputStyle} /></Field>
              <Field label="Telefon"><input value={form.telefon || ''} onChange={e => setForm(p => ({...p, telefon: e.target.value}))} style={inputStyle} /></Field>
              <Field label="Mobil"><input value={form.mobil || ''} onChange={e => setForm(p => ({...p, mobil: e.target.value}))} style={inputStyle} /></Field>
              <Field label="Abteilung"><input value={form.abteilung || ''} onChange={e => setForm(p => ({...p, abteilung: e.target.value}))} style={inputStyle} /></Field>
              <Field label="Rolle">
                <select value={form.rolle || ''} onChange={e => setForm(p => ({...p, rolle: e.target.value}))} style={inputStyle}>
                  {ROLLEN.map(r => <option key={r}>{r}</option>)}
                </select>
              </Field>
              <Field label="Sprache"><input value={form.sprache || ''} onChange={e => setForm(p => ({...p, sprache: e.target.value}))} style={inputStyle} /></Field>
            </div>
            <Field label="Notizen" style={{ marginTop: 10 }}>
              <textarea value={form.notizen || ''} onChange={e => setForm(p => ({...p, notizen: e.target.value}))} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
            </Field>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button onClick={handleSave} disabled={saving} className="btn-primary">{saving ? '…' : 'Speichern'}</button>
              <button onClick={() => setEditMode(false)} className="btn-secondary">Abbrechen</button>
              <button onClick={handleDelete} className="btn-danger" style={{ marginLeft: 'auto' }}>Kontakt löschen</button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 16 }}>
            <Card title="Kontaktdaten">
              {kontakt.email && <Row label="E-Mail" value={<a href={`mailto:${kontakt.email}`} style={{ color: '#818cf8' }}>{kontakt.email}</a>} />}
              {kontakt.telefon && <Row label="Telefon" value={kontakt.telefon} />}
              {kontakt.mobil && <Row label="Mobil" value={kontakt.mobil} />}
              {kontakt.fax && <Row label="Fax" value={kontakt.fax} />}
              {kontakt.sprache && <Row label="Sprache" value={kontakt.sprache} />}
            </Card>
            <Card title="Zuordnung">
              {kontakt.anbieter_name && <Row label="Anbieter" value={kontakt.anbieter_name} />}
              {kontakt.vertrag_titel && <Row label="Vertrag" value={kontakt.vertrag_titel} />}
              <Row label="Rolle" value={kontakt.rolle} />
              {kontakt.abteilung && <Row label="Abteilung" value={kontakt.abteilung} />}
            </Card>
            {kontakt.notizen && (
              <Card title="Notizen">
                <p style={{ color: '#94a3b8', fontSize: 13, whiteSpace: 'pre-wrap', margin: 0 }}>{kontakt.notizen}</p>
              </Card>
            )}
          </div>
        )
      )}

      {/* Tab 1: Historie */}
      {tab === 1 && (
        <div>
          {historie.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#64748b', padding: 40 }}>Keine Änderungshistorie.</div>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {historie.map(h => (
                <div key={h.id} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '12px 14px' }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{ width: 32, height: 32, background: '#334155', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
                      {h.aktion === 'erstellt' ? '✨' : h.aktion === 'geloescht' ? '🗑' : '✏️'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{h.aktion}</div>
                      {h.feldname && <div style={{ color: '#64748b', fontSize: 12 }}>Feld: {h.feldname}</div>}
                      {h.alter_wert != null && <div style={{ color: '#94a3b8', fontSize: 12 }}>Alt: {h.alter_wert}</div>}
                      {h.neuer_wert != null && <div style={{ color: '#94a3b8', fontSize: 12 }}>Neu: {h.neuer_wert}</div>}
                      {h.notiz && <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>{h.notiz}</div>}
                    </div>
                    <div style={{ color: '#475569', fontSize: 12, flexShrink: 0 }}>
                      {h.erstellt_am ? new Date(h.erstellt_am).toLocaleString('de-DE') : '—'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 16 }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</h3>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #0f172a', fontSize: 13 }}>
      <span style={{ color: '#64748b', flexShrink: 0, marginRight: 12 }}>{label}</span>
      <span style={{ color: '#f1f5f9', textAlign: 'right', wordBreak: 'break-word' }}>{value || '—'}</span>
    </div>
  );
}

function Field({ label, children, style }) {
  return (
    <div style={{ marginBottom: 4, ...style }}>
      <label style={{ display: 'block', color: '#94a3b8', fontSize: 12, marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  );
}

const btn = (bg) => ({ background: bg, color: '#f1f5f9', border: 'none', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13 });
const inputStyle = { background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', padding: '8px 10px', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box' };
